import axios from "axios";
import { API_BASE_URL } from "./config/integrations";
import { normalizeMediaPayload } from "./utils/mediaUrls";

export const API_LOADING_EVENT = "advahini:api-loading";

/**
 * Axios instance
 */
console.log('REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

let activeRequestCount = 0;
const activeRequestMessages = new Map();

const emitApiLoadingState = () => {
  if (typeof window === "undefined") return;
  const latestMessage = Array.from(activeRequestMessages.values()).pop() || "Working...";
  window.dispatchEvent(
    new CustomEvent(API_LOADING_EVENT, {
      detail: {
        pending: activeRequestCount,
        message: latestMessage,
      },
    })
  );
};

const parseRequestData = (data) => {
  if (!data || typeof data !== "string") {
    return data || {};
  }
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const describeApiAction = (config = {}) => {
  const url = String(config.url || "");
  const method = String(config.method || "get").toLowerCase();
  const data = parseRequestData(config.data);

  if (url.includes("/content/upload")) return "Uploading asset...";
  if (url.includes("/screens/claim")) return "Connecting screen...";
  if (url.includes("/screens/") && method !== "get") return "Saving screen...";
  if (url.includes("/playlists") && method !== "get") return "Saving playlist...";
  if (url.includes("/schedules") && method !== "get") {
    return Object.prototype.hasOwnProperty.call(data, "fallback_playlist_id")
      ? "Saving fallback playlist..."
      : "Saving schedule...";
  }
  if (url.includes("/workspaces") && method !== "get") return "Saving workspace...";
  if (url.includes("/billing") && method !== "get") return "Saving billing changes...";
  if (method === "get") return "Loading data...";
  if (method === "delete") return "Deleting...";
  return "Saving changes...";
};

const shouldTrackApiLoading = (config = {}) => {
  if (typeof window === "undefined") return false;
  if (config.advahiniSkipGlobalLoading) return false;
  if (config.advahiniGlobalLoading) return true;
  const url = String(config.url || "");
  const method = String(config.method || "get").toLowerCase();

  if (url.includes("/content/upload")) return false;
  if (url.startsWith("/player") || url.startsWith("/auth")) return false;
  if (["get", "head", "options"].includes(method)) return false;

  return true;
};

const beginApiLoading = (config = {}) => {
  if (!shouldTrackApiLoading(config)) return config;
  const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  config.advahiniLoadingToken = token;
  activeRequestCount += 1;
  activeRequestMessages.set(token, describeApiAction(config));
  emitApiLoadingState();
  return config;
};

const endApiLoading = (config = {}) => {
  const token = config?.advahiniLoadingToken;
  if (!token) return;
  activeRequestMessages.delete(token);
  activeRequestCount = Math.max(0, activeRequestCount - 1);
  emitApiLoadingState();
};

const getStoredWorkspaceId = () => {
  if (typeof window === "undefined") {
    return "";
  }
  return String(window.localStorage.getItem("workspaceId") || "").trim();
};

const normalizeResponseData = (response) => {
  if (!response || typeof response !== "object") {
    return response;
  }
  return {
    ...response,
    data: normalizeMediaPayload(response.data),
  };
};

/**
 * ---------------------------------------
 * REQUEST INTERCEPTOR
 * ---------------------------------------
 */
api.interceptors.request.use(
  (config) => {
    beginApiLoading(config);
    const url = config.url || "";
    config.headers = config.headers || {};

    const skipWorkspaceHeader =
      ((url === "/workspaces" || url === "/workspaces/") && config.method === "get") ||
      ((url === "/workspaces" || url === "/workspaces/") && config.method === "post") ||
      url === "/screens/claim" ||      // ✅ screen activation
      url.startsWith("/player");       // ✅ public player endpoints

    if (skipWorkspaceHeader) {
      delete config.headers["X-Workspace-Id"];
      return config;
    }

    const explicitWorkspaceId = String(config.headers["X-Workspace-Id"] || "").trim();
    const workspaceId = explicitWorkspaceId || getStoredWorkspaceId();
    if (workspaceId) {
      config.headers["X-Workspace-Id"] = workspaceId;
    } else {
      delete config.headers["X-Workspace-Id"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * ---------------------------------------
 * RESPONSE INTERCEPTOR
 * ---------------------------------------
 */
api.interceptors.response.use(
  (response) => {
    endApiLoading(response.config);
    return response;
  },
  (error) => {
    endApiLoading(error?.config);
    return Promise.reject(error);
  }
);

/**
 * ---------------------------------------
 * AUTH TOKEN HANDLER
 * ---------------------------------------
 */
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

export const getCurrentUser = () => api.get("/auth/me");
export const loginLocal = (email, password) =>
  api.post("/auth/login", { email, password });
export const registerLocal = ({ email, password, fullName, companyName }) =>
  api.post("/auth/register", {
    email,
    password,
    full_name: fullName,
    company_name: companyName,
  });
export const googleAuthLocal = ({ credential, companyName = "" }) =>
  api.post("/auth/google", {
    credential,
    company_name: companyName,
  });
/**
 * ---------------------------------------
 * LANGUAGE (USER)
 * ---------------------------------------
 */
export const getLanguages = () => api.get("/languages");
export const getUserLanguage = () => api.get("/users/me/language");
export const updateUserLanguage = (languageCode) =>
  api.put("/users/me/language", { language_code: languageCode });
export const updateMyProfile = (data) => api.put("/users/me/profile", data);
export const updateMyCompany = (data) => api.put("/users/me/company", data);

/**
 * ---------------------------------------
 * BILLING / UPGRADE (USER)
 * ---------------------------------------
 */
export const getUpgradePlans = (params = {}, config = {}) =>
  api.get("/billing/plans", { ...config, params });
export const validateUpgradeCoupon = (data) => api.post("/billing/coupons/validate", data);
export const submitSalesContact = (data) => api.post("/marketing/sales-contact", data);
export const getPublicPage = (slug) => api.get(`/pages/${slug}`);
export const getPublicBlogPosts = (params = {}, config = {}) =>
  api.get("/blog", { ...config, params });
export const getPublicBlogPost = (slug) => api.get(`/blog/${slug}`);
export const getUserBillingPreferences = () => api.get("/users/me/preferences");
export const updateUserBillingPreferences = (data) =>
  api.put("/users/me/preferences", data);
export const createBillingCheckoutSession = (data) =>
  api.post("/billing/checkout/session", data);
export const verifyPaddleCheckoutTransaction = (transactionId) =>
  api.post("/billing/paddle/verify-transaction", { transaction_id: transactionId });
export const confirmRazorpayCheckout = (data) =>
  api.post("/billing/razorpay/confirm", data);
export const createUpgradePaymentIntent = (data) => api.post("/billing/payments/intent", data);
export const confirmUpgradePayment = (data) => api.post("/billing/payments/confirm", data);
export const createUpgradeSubscription = (data) => api.post("/billing/subscriptions/upgrade", data);
export const getMySubscription = () => api.get("/billing/subscriptions/me");
export const cancelMySubscription = (data = {}) => api.post("/billing/subscriptions/me/cancel", data);
export const getMyInvoices = (params = {}) => api.get("/billing/invoices", { params });
export const getMyInvoicePdf = (invoiceId) =>
  api.get(`/billing/invoices/${invoiceId}/pdf`, { responseType: "blob" });

/**
 * ---------------------------------------
 * WORKSPACE APIs
 * ---------------------------------------
 */
export const getWorkspaces = () => api.get("/workspaces");
export const createWorkspace = (name, description) =>
  api.post("/workspaces/", { name, description });
export const updateWorkspace = async (id, data) => {
  try {
    return await api.patch(`/workspaces/${id}`, data);
  } catch (err) {
    return api.put(`/workspaces/${id}`, data);
  }
};
export const deleteWorkspace = async (id) => {
  try {
    return await api.delete(`/workspaces/${id}`);
  } catch (err) {
    return api.delete(`/workspaces/${id}/`);
  }
};

const tryTeamEndpoint = async (requests) => {
  let lastError;
  for (const request of requests) {
    try {
      return await request();
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      if (status && ![404, 405].includes(status)) {
        throw err;
      }
    }
  }
  throw lastError;
};

const tryCanvaEndpoint = async (requests) => {
  let lastError;
  for (const request of requests) {
    try {
      return await request();
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      if (status && ![404, 405].includes(status)) {
        throw err;
      }
    }
  }
  throw lastError;
};

export const getTeamMembers = (workspaceId) =>
  tryTeamEndpoint([
    () => api.get(`/workspaces/${workspaceId}/members`),
    () => api.get(`/workspaces/${workspaceId}/team`),
    () => api.get("/team/members", { params: { workspace_id: workspaceId } }),
    () => api.get("/workspace-members", { params: { workspace_id: workspaceId } }),
  ]);

export const createTeamMember = (data) =>
  tryTeamEndpoint([
    () => api.post(`/workspaces/${data.workspace_id}/members`, data),
    () => api.post(`/workspaces/${data.workspace_id}/team`, data),
    () => api.post("/team/members", data),
    () => api.post("/workspace-members", data),
  ]);

/**
 * ---------------------------------------
 * CANVA CONNECT
 * ---------------------------------------
 * Backend owns OAuth PKCE, token storage, export polling, and downloading
 * temporary Canva export URLs into Advahini content storage.
 */
export const getCanvaConnectStatus = () =>
  tryCanvaEndpoint([
    () => api.get("/integrations/canva/status"),
    () => api.get("/canva/status"),
    () => api.get("/canva/isauthorized"),
  ]);

export const createCanvaDesignSession = (data = {}) =>
  tryCanvaEndpoint([
    () => api.post("/integrations/canva/design-session", data),
    () => api.post("/canva/design-session", data),
    () => api.post("/canva/designs/session", data),
  ]);

export const importCanvaDesignReturn = (data = {}) =>
  tryCanvaEndpoint([
    () => api.post("/integrations/canva/import-return", data, { timeout: 180000 }),
    () => api.post("/canva/import-return", data, { timeout: 180000 }),
    () => api.post("/canva/exports/import-return", data, { timeout: 180000 }),
  ]);

export const disconnectCanvaConnect = () =>
  tryCanvaEndpoint([
    () => api.delete("/integrations/canva/connection"),
    () => api.delete("/canva/connection"),
  ]);

export const deleteTeamMember = (workspaceId, memberId) =>
  tryTeamEndpoint([
    () => api.delete(`/workspaces/${workspaceId}/members/${memberId}`),
    () => api.delete(`/workspaces/${workspaceId}/team/${memberId}`),
    () => api.delete(`/team/members/${memberId}`, { data: { workspace_id: workspaceId } }),
    () => api.delete(`/workspace-members/${memberId}`, { data: { workspace_id: workspaceId } }),
  ]);

/**
 * ---------------------------------------
 * ANALYTICS
 * ---------------------------------------
 */
export const getOverview = () => api.get("/analytics/overview");
export const getAnalyticsUsage = (params = {}) => api.get("/analytics/usage", { params });

/**
 * ---------------------------------------
 * SCREENS
 * ---------------------------------------
 */
export const createScreen = (data) => api.post("/screens/", data);
export const getScreens = () => api.get("/screens/");
export const updateScreen = (id, updates, config = {}) =>
  api.patch(`/screens/${id}`, updates, config);
export const deleteScreen = (id) => api.delete(`/screens/${id}`);
export const remapScreenPlayer = (id, data, config = {}) =>
  api.post(`/screens/${id}/remap-player`, data, config);

/** ✅ FIXED: activation_code based claim */
export const screenClaim = (data, config = {}) =>
  api.post("/screens/claim", data, config);

/**
 * ---------------------------------------
 * CONTENT
 * ---------------------------------------
 */
export const uploadContent = (
  file,
  name,
  duration = 10,
  scope = "workspace",
  folderId = null,
  options = {}
) => {
  const formData = new FormData();
  formData.append("file", file);
  if (name) formData.append("name", name);
  formData.append("duration", duration);

  const params = {};
  if (scope === "global") params.scope = "global";
  if (folderId) params.folder_id = folderId;

  return api
    .post("/content/upload", formData, {
      params,
      timeout: 900000,
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: options.onUploadProgress,
      advahiniSkipGlobalLoading: true,
    })
    .then(normalizeResponseData);
};

export const getContent = (scope = "workspace", folderId = null) => {
  return api
    .get("/content", {
      params: {
        ...(scope === "global" ? { scope: "global" } : {}),
        ...(folderId ? { folder_id: folderId } : {}),
      },
      timeout: 60000,
    })
    .then(normalizeResponseData);
};

export const deleteContent = (id, scope = "workspace") => {
  return api.delete(`/content/${id}`, {
    params: scope === "global" ? { scope: "global" } : undefined,
  });
};

export const transcodeContentAndroidSafe = (id, scope = "workspace") =>
  api.post(`/content/${id}/transcode/android-safe`, null, {
    params: scope === "global" ? { scope: "global" } : undefined,
    timeout: 900000,
  });

export const createMediaFolder = async ({
  name,
  parentId = null,
  scope = "workspace",
} = {}) => {
  const folderName = String(name || "").trim();
  if (!folderName) {
    throw new Error("Folder name is required");
  }

  const endpoints = [
    "/media/folders",
    "/media/folders/",
    "/content/folders",
    "/content/folders/",
    "/folders",
    "/folders/",
  ];
  const payloads = [
    { name: folderName, parent_id: parentId },
    { folder_name: folderName, parent_id: parentId },
    { name: folderName, parentId },
    { folderName: folderName, parentId },
  ];
  const config =
    scope === "global"
      ? { params: { scope: "global" } }
      : {};

  let lastError;
  for (const endpoint of endpoints) {
    for (const payload of payloads) {
      try {
        return await api.post(endpoint, payload, config);
      } catch (err) {
        lastError = err;
      }
    }
  }
  throw lastError;
};

export const listMediaFolders = async ({ scope = "workspace", parentId = null } = {}) => {
  const endpoints = [
    "/media/folders",
    "/media/folders/",
    "/content/folders",
    "/content/folders/",
    "/folders",
    "/folders/",
  ];

  const config = {
    params: {
      ...(scope === "global" ? { scope: "global" } : {}),
      ...(parentId ? { parent_id: parentId } : {}),
    },
  };

  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, config);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};

/**
 * ---------------------------------------
 * PLAYLISTS
 * ---------------------------------------
 */
export const createPlaylist = (data) =>
  api.post("/playlists", {
    name: data.name,
    content_ids: data.content_ids,
    loop: data.loop || false,
    app_scenes: data.app_scenes || data.scene_configs || [],
  }, { timeout: 120000 });

export const getPlaylists = () => api.get("/playlists");

export const deletePlaylist = async (playlistId) => {
  try {
    return await api.delete(`/playlists/${playlistId}`);
  } catch (err) {
    return api.delete(`/playlists/${playlistId}/`);
  }
};

export const getSchedules = async () => {
  const endpoints = ["/schedules", "/schedules/", "/schedule-groups", "/schedule_groups"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      const res = await api.get(endpoint);
      const normalizedData = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      return { ...res, data: normalizedData };
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};

export const createSchedule = (data) =>
  api.post("/schedules", {
    name: data.name,
    group_id: data.group_id || null,
    events: data.events || [],
    fallback_playlist_id: data.fallback_playlist_id || null,
  });

export const updateSchedule = (scheduleId, data) =>
  api.patch(`/schedules/${scheduleId}`, data);

export const deleteSchedule = (scheduleId) => api.delete(`/schedules/${scheduleId}`);

export const duplicateSchedule = (scheduleId) => api.post(`/schedules/${scheduleId}/duplicate`);

export const addScheduleEvent = (scheduleId, data) =>
  api.post(`/schedules/${scheduleId}/events`, data);

export const updateScheduleEvent = (scheduleId, eventId, data) =>
  api.patch(`/schedules/${scheduleId}/events/${eventId}`, data);

export const deleteScheduleEvent = (scheduleId, eventId) =>
  api.delete(`/schedules/${scheduleId}/events/${eventId}`);

export const getSequences = async () => {
  const endpoints = ["/sequences", "/sequences/", "/sequence-groups", "/sequence_groups"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      const res = await api.get(endpoint);
      const normalizedData = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      return { ...res, data: normalizedData };
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};

export const updatePlaylist = (playlistId, data) =>
  api.patch(`/playlists/${playlistId}`, {
    name: data.name,
    content_ids: data.content_ids,
    loop: data.loop,
    app_scenes: data.app_scenes || data.scene_configs || [],
  }, { timeout: 120000 });

  
export const getPlaylistForScreen = (screenId) =>
  api.get(`/playlists/screen/${screenId}`);

export const getPlaylistScenes = (playlistId) =>
  api.get(`/playlists/${playlistId}/scenes`);

export const createPlaylistScene = (playlistId, data) =>
  api.post(`/playlists/${playlistId}/scenes`, data);

export const updatePlaylistScene = (playlistId, sceneId, data) =>
  api.patch(`/playlists/${playlistId}/scenes/${sceneId}`, data);

export const deletePlaylistScene = (playlistId, sceneId) =>
  api.delete(`/playlists/${playlistId}/scenes/${sceneId}`);

export const reorderPlaylistScenes = (playlistId, data) =>
  api.patch(`/playlists/${playlistId}/scenes/reorder`, data);

/**
 * Assign playlist / schedule / sequence to screen
 */
export const assignProgramToScreen = async (screenId, assignment, config = {}) => {
  const normalizedType = String(assignment?.type || "playlist").toLowerCase();
  const targetId = String(assignment?.id || "").trim();
  const targetTypeKey =
    normalizedType === "schedule"
      ? "schedule"
      : normalizedType === "sequence"
        ? "sequence"
        : "playlist";

  if (!targetId) {
    throw new Error("assignment id is required");
  }

  const payload = {
    assignment_type: targetTypeKey,
    assignment_id: targetId,
  };

  try {
    return await api.patch(`/screens/${screenId}/assignment`, payload, config);
  } catch (err) {
    const status = Number(err?.response?.status || 0);
    if (![404, 405].includes(status)) {
      throw err;
    }
  }

  return api.patch(`/screens/${screenId}`, payload, config);
};

export const assignPlaylistToScreen = async (screenId, playlistId) =>
  assignProgramToScreen(screenId, { type: "playlist", id: playlistId });

export const getScreenZones = (screenId) => api.get(`/screens/${screenId}/zones`);
export const updateScreenZones = (screenId, data) =>
  api.put(`/screens/${screenId}/zones`, data);
export const getWeatherCurrent = async (city, units = "c", requestConfig = {}) => {
  const endpoints = ["/screens/apps/weather/current", "/apps/weather/current"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, {
        ...requestConfig,
        params: { city, units },
      });
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED") {
        throw err;
      }
      lastError = err;
    }
  }
  throw lastError;
};
export const getAirQualityLocationSearch = async (query, limit = 5, requestConfig = {}) => {
  return api.get("/apps/air-quality/search", {
    ...requestConfig,
    params: { q: query, limit },
    advahiniSkipGlobalLoading: true,
  });
};
export const getCurrencyRateCurrencies = async (requestConfig = {}) => {
  return api.get("/apps/currency-rates/currencies", {
    ...requestConfig,
    advahiniSkipGlobalLoading: true,
  });
};
export const getDaysOfWorldCurrent = async (requestConfig = {}) => {
  const endpoints = ["/screens/apps/days-of-the-world/current", "/apps/days-of-the-world/current"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, requestConfig);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};
export const getWordOfDayCurrent = async (requestConfig = {}) => {
  const endpoints = ["/screens/apps/word-of-the-day/current", "/apps/word-of-the-day/current"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, requestConfig);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};
export const getAiQuizCurrent = async (requestConfig = {}) => {
  const endpoints = ["/screens/apps/ai-quiz/current", "/apps/ai-quiz/current"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, requestConfig);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};
export const getSocialIntegrationStatus = () => api.get("/apps/social-status");
export const getSocialFeedPreview = (params = {}) => api.get("/apps/social-feed", { params });
export const connectSocialAccount = (platform, params = {}) => api.get(`/social/connect/${platform}`, { params });
export const getSocialAccounts = () => api.get("/social/accounts");
export const disconnectSocialAccount = (accountId) => api.delete(`/social/accounts/${accountId}`);
export const syncSocialAccount = (accountId, params = {}) => api.post(`/social/accounts/${accountId}/sync`, null, { params });
export const getSocialAccountPosts = (accountId, params = {}) => api.get(`/social/accounts/${accountId}/posts`, { params });
export const createPlaylistSocialScene = (playlistId, data) =>
  api.post(`/playlists/${playlistId}/social-scenes`, data);
const googleBusinessReviewsDisabled = () =>
  Promise.reject(new Error("Google Business Reviews integration is temporarily disabled."));

// Google Business Reviews connection endpoints are temporarily disabled.
export const connectGoogleBusiness = googleBusinessReviewsDisabled;
export const getGoogleBusinessAccounts = () => Promise.resolve({ data: { connections: [] } });
export const getGoogleBusinessLocations = () => Promise.resolve({ data: { locations: [] } });
export const selectGoogleBusinessLocation = googleBusinessReviewsDisabled;
export const syncGoogleBusinessReviews = googleBusinessReviewsDisabled;
export const getGoogleBusinessReviews = () => Promise.resolve({ data: { reviews: [] } });
export const updateGoogleBusinessReviewVisibility = googleBusinessReviewsDisabled;
export const getZoneTemplates = async () => {
  const endpoints = [
    "/zone-templates",
    "/zone-templates/",
    "/screens/zone-templates",
    "/screens/zone-templates/",
  ];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};
/**
 * ---------------------------------------
 * PLAYER (PUBLIC)
 * ---------------------------------------
 */

/** GET /player/{activation_code} */
export const getPlayerPlaylist = async (activationCode) => {
  try {
    const response = await api.get(`/player/${activationCode}`);
    return response.data || { content: [] };
  } catch (error) {
    console.error("Player playlist error:", error);
    return { content: [] };
  }
};

/** POST /player/register */
export const initPlayer = async () => {
  let res;
  try {
    res = await api.post("/player/register");
  } catch (err) {
    if (![404, 405].includes(err?.response?.status)) {
      throw err;
    }
    res = await api.get("/player");
  }
  console.log("Player init response:", res);
  return res.data;
};

export const getPlayerStatus = async (screenUid) => {
  const res = await api.get(`/player/${screenUid}`);
  return res.data;
};

export const registerPlayerDevice = (data) => api.post("/player/devices/register", data);

export const updatePlayerDeviceSync = (deviceId, data) =>
  api.post(`/player/devices/${deviceId}/sync`, data);

export const pollPlayer = (screenUid, version, deviceTimezone, deviceId) =>
  api.get(`/player/poll`, {
    params: {
      screen_uid: screenUid,
      version: version,
      ...(deviceId ? { device_id: deviceId } : {}),
      ...(deviceTimezone
        ? { device_tz: deviceTimezone }
        : typeof Intl !== "undefined" && Intl.DateTimeFormat
          ? { device_tz: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined }
          : {}),
    },
  });

export const sendPlayerHeartbeat = (screenUid, deviceId) =>
  api.post("/player/heartbeat", null, {
    params: {
      screen_uid: screenUid,
      ...(deviceId ? { device_id: deviceId } : {}),
    },
  });

export const disconnectPlayerDevice = (screenUid) =>
  api.post("/player/disconnect", { screen_uid: screenUid });

export const trackPlayerEvent = (data) => api.post("/player/events", data);

/**
 * ---------------------------------------
 * ADMIN API FUNCTIONS (UNCHANGED)
 * ---------------------------------------
 */
const adminListParams = (params = {}) => ({
  limit: 10,
  offset: 0,
  ...params,
});

export const getAdminDashboardStats = () => api.get("/admin/dashboard/stats");
export const getAdminDashboardActivity = (params = {}) =>
  api.get("/admin/dashboard/activity", { params: adminListParams({ limit: 25, ...params }) });
export const getAdminDashboardExpirations = (params = {}) =>
  api.get("/admin/dashboard/expirations", { params });
export const getAdminNewsletterSettings = () => api.get("/admin/newsletter/settings");
export const saveAdminNewsletterSettings = (data) =>
  api.post("/admin/newsletter/settings", data);
export const sendAdminNewsletter = (data) => api.post("/admin/newsletter/send", data);
export const runAdminWeeklyNewsletter = () => api.post("/admin/newsletter/run-weekly");
export const sendAdminTrialReminders = () => api.post("/admin/trial-reminders/send");
export const getSubscriptions = (params = {}) =>
  api.get("/admin/subscriptions", { params: adminListParams(params) });
export const getSubscription = (id) =>
  api.get(`/admin/subscriptions/${id}`);
export const updateSubscription = (id, data) =>
  api.put(`/admin/subscriptions/${id}`, data);
export const extendTrialSubscription = (id, data) =>
  api.post(`/admin/subscriptions/${id}/extend-trial`, data);
export const activateSubscription = (id) =>
  api.post(`/admin/subscriptions/${id}/activate`);
export const cancelSubscription = (id) =>
  api.post(`/admin/subscriptions/${id}/cancel`);
export const getPayments = (params = {}) =>
  api.get("/admin/payments", { params: adminListParams(params) });
export const createPayment = (data) =>
  api.post("/admin/payments", data);
export const updatePayment = (id, data) =>
  api.put(`/admin/payments/${id}`, data);
export const reconcilePayment = (id) =>
  api.post(`/admin/payments/${id}/reconcile`);
export const reconcileRecentPayments = (params = {}) =>
  api.post("/admin/billing/reconcile", null, { params });
export const getWebhookEvents = (params = {}) =>
  api.get("/admin/webhook-events", { params: adminListParams(params) });
export const retryWebhookEvent = (id) =>
  api.post(`/admin/webhook-events/${id}/retry`);
export const getAdminPlayerDevices = async (params = {}) => {
  const endpoints = [
    "/admin/player-devices",
    "/admin/player/devices",
    "/admin/screens/player-devices",
  ];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, { params });
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};
export const getAdminPlaylists = async (params = {}) => {
  const endpoints = ["/admin/playlists", "/playlists"];
  let lastError;
  for (const endpoint of endpoints) {
    try {
      return await api.get(endpoint, { params });
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};
export const getSubscriptionPlans = (params = {}) =>
  api.get("/admin/plans", { params: adminListParams(params) });
export const getAdminBillingCurrencies = (params = {}) =>
  api.get("/admin/billing/currencies", { params: adminListParams(params) });
export const createAdminBillingCurrency = (data) => api.post("/admin/billing/currencies", data);
export const updateAdminBillingCurrency = (code, data) =>
  api.patch(`/admin/billing/currencies/${code}`, data);
export const getAdminBillingPricing = (params = {}) =>
  api.get("/admin/billing/pricing", { params: adminListParams(params) });
export const createAdminBillingPricing = (data) => api.post("/admin/billing/pricing", data);
export const updateAdminBillingPricing = (id, data) =>
  api.patch(`/admin/billing/pricing/${id}`, data);
export const getAllScreens = (params = {}) =>
  api.get("/admin/screens", { params: adminListParams(params) });
export const getScreen = (id) => api.get(`/admin/screens/${id}`);
export const updateAdminScreen = (id, data) =>
  api.patch(`/admin/screens/${id}`, data);
export const revokeScreen = (id) =>
  api.post(`/admin/screens/${id}/revoke`);
export const activateScreen = (id) =>
  api.post(`/admin/screens/${id}/activate`);
export const deactivateScreen = (id) =>
  api.post(`/admin/screens/${id}/deactivate`);
export const getPendingContent = (params = {}) =>
  api.get("/admin/content/pending-review", { params: adminListParams(params) });
export const getAllContent = (params = {}) =>
  api.get("/admin/content", { params: adminListParams(params) });
export const getContentDetails = (id) =>
  api.get(`/admin/content/${id}`);
export const reviewContent = (id, data) =>
  api.post(`/admin/content/${id}/review`, data);
export const getAdminUsers = (params = {}, config = {}) =>
  api.get("/admin/users", {
    ...config,
    params: {
      limit: 10,
      offset: 0,
      ...adminListParams(params),
      ...(config.params || {}),
    },
    timeout: config.timeout || 60000,
  });
export const deleteAdminUser = (userId) =>
  api.delete(`/admin/users/${userId}`);
export const assignRoleToUser = (userId, data) =>
  api.post(`/admin/users/${userId}/roles`, data);
export const getUserRoles = (userId) =>
  api.get(`/admin/users/${userId}/roles`);
export const removeRoleFromUser = (userId, userRoleId) =>
  api.delete(`/admin/users/${userId}/roles/${userRoleId}`);

/**
 * ---------------------------------------
 * ADMIN LANGUAGE MANAGEMENT
 * ---------------------------------------
 */
export const getAdminLanguages = (params = {}) => api.get("/admin/languages", { params: adminListParams(params) });
export const createAdminLanguage = (data) => api.post("/admin/languages", data);
export const updateAdminLanguage = (id, data) => api.patch(`/admin/languages/${id}`, data);
export const deleteAdminLanguage = (id) => api.delete(`/admin/languages/${id}`);
export const setAdminDefaultLanguage = (id) =>
  api.post(`/admin/languages/${id}/set-default`);
export const getAdminSitePages = (params = {}) => api.get("/admin/site-pages", { params: adminListParams(params) });
export const createAdminSitePage = (data) => api.post("/admin/site-pages", data);
export const updateAdminSitePage = (id, data) => api.patch(`/admin/site-pages/${id}`, data);
export const deleteAdminSitePage = (id) => api.delete(`/admin/site-pages/${id}`);
export const getAdminBlogPosts = (params = {}) => api.get("/admin/blog-posts", { params: adminListParams(params) });
export const createAdminBlogPost = (data) => api.post("/admin/blog-posts", data);
export const updateAdminBlogPost = (id, data) => api.patch(`/admin/blog-posts/${id}`, data);
export const deleteAdminBlogPost = (id) => api.delete(`/admin/blog-posts/${id}`);

export const getNewsFeedPreview = (data, config = {}) =>
  api.post("/apps/bbc-news/preview", data, {
    ...config
  });
export const getBbcNewsPreview = getNewsFeedPreview;
/**
 * ---------------------------------------
 * DEFAULT EXPORT
 * ---------------------------------------
 */
const apiClient = {
  ...api,
  setAuthToken,
  getCurrentUser,
  loginLocal,
  registerLocal,
  googleAuthLocal,
  getLanguages,
  getUserLanguage,
  updateUserLanguage,
  updateMyProfile,
  updateMyCompany,
  getCanvaConnectStatus,
  createCanvaDesignSession,
  importCanvaDesignReturn,
  disconnectCanvaConnect,
  getUpgradePlans,
  validateUpgradeCoupon,
  submitSalesContact,
  getPublicPage,
  getPublicBlogPosts,
  getPublicBlogPost,
  getUserBillingPreferences,
  updateUserBillingPreferences,
  createBillingCheckoutSession,
  confirmRazorpayCheckout,
  createUpgradePaymentIntent,
  confirmUpgradePayment,
  createUpgradeSubscription,
  getMySubscription,
  cancelMySubscription,
  getMyInvoices,
  getMyInvoicePdf,
  getOverview,
  getAnalyticsUsage,
  createScreen,
  getScreens,
  remapScreenPlayer,
  deleteScreen,
  uploadContent,
  getContent,
  deleteContent,
  transcodeContentAndroidSafe,
  createMediaFolder,
  listMediaFolders,
  createPlaylist,
  getPlaylists,
  getSchedules,
  getSequences,
  getPlaylistForScreen,
  getPlaylistScenes,
  createPlaylistScene,
  updatePlaylistScene,
  deletePlaylistScene,
  reorderPlaylistScenes,
  assignProgramToScreen,
  assignPlaylistToScreen,
  getScreenZones,
  updateScreenZones,
  getWeatherCurrent,
  getAirQualityLocationSearch,
  getCurrencyRateCurrencies,
  getDaysOfWorldCurrent,
  getWordOfDayCurrent,
  getSocialIntegrationStatus,
  getSocialFeedPreview,
  connectSocialAccount,
  disconnectSocialAccount,
  getSocialAccounts,
  syncSocialAccount,
  getSocialAccountPosts,
  createPlaylistSocialScene,
  getZoneTemplates,
  getPlayerPlaylist,
  getAdminNewsletterSettings,
  saveAdminNewsletterSettings,
  sendAdminNewsletter,
  runAdminWeeklyNewsletter,
  sendAdminTrialReminders,
  screenClaim,
  registerPlayerDevice,
  updatePlayerDeviceSync,
  pollPlayer,
  sendPlayerHeartbeat,
  trackPlayerEvent,
  initPlayer,
  getPlayerStatus,
  getAdminLanguages,
  createAdminLanguage,
  updateAdminLanguage,
  deleteAdminLanguage,
  setAdminDefaultLanguage,
  getAdminSitePages,
  createAdminSitePage,
  updateAdminSitePage,
  deleteAdminSitePage,
  getAdminBlogPosts,
  createAdminBlogPost,
  updateAdminBlogPost,
  deleteAdminBlogPost,
  getAdminBillingCurrencies,
  createAdminBillingCurrency,
  updateAdminBillingCurrency,
  getAdminBillingPricing,
  createAdminBillingPricing,
  updateAdminBillingPricing,
  deleteAdminUser,
  getAdminPlayerDevices,
  getAdminPlaylists,
  extendTrialSubscription,
  updateAdminScreen,
  deactivateScreen,
  getNewsFeedPreview,
  getBbcNewsPreview,
  getDaysOfWorldCurrent,
  getWordOfDayCurrent
};

export default apiClient;
